"use client";

// #432: out-of-state shipping selection at signup. Replaces the old #235
// fulfillment-only questionnaire with the approved 3-method selector
// (SignupShippingSelector) so a new out-of-FL client sets their DEFAULT shipping
// method up front (DOOR / AIRPORT / OWN_CARRIER). The old #235 carrier/trucker
// capture (incl. the "help me set this up" concierge escape hatch) is preserved
// UNDER "Use my own carrier" (OWN_CARRIER). Renders ONLY when out-of-state; FL
// signups never see it.

import { useFormContext } from "react-hook-form";
import type { SignupFormValues } from "./SignupWizard";
import { fulfillmentOptions } from "@/lib/delivery/fulfillment";
import type { ShippingMethod } from "@/lib/delivery/out-of-state-pricing";
import { suggestAirport } from "@/lib/delivery/us-airports";
import { SignupShippingSelector } from "./SignupShippingSelector";
import { fieldClass, labelClass, radioCardClass, radioCardSelectedClass } from "./styles";

export function SignupFulfillment({
  isOutOfState,
  locale = "en",
}: {
  isOutOfState: boolean;
  locale?: "en" | "es";
}) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<SignupFormValues>();

  if (!isOutOfState) return null;

  const method = (watch("shippingMethod") || "AIRPORT") as ShippingMethod;
  const storedAirport = watch("shippingAirport");
  const airportConfirmed = watch("shippingAirportConfirmed");
  const zip = watch("zip");
  const state = watch("state");
  const suggested = suggestAirport({ zip, state });

  const choice = watch("fulfillment.choice");
  const fErr = errors.fulfillment;

  return (
    <div
      data-testid="signup-fulfillment"
      className="mt-6 rounded-xl border border-[#77C1BB]/40 bg-[#77C1BB]/5 p-4"
    >
      <SignupShippingSelector
        locale={locale === "es" ? "es" : "en"}
        value={{
          method,
          airport:
            storedAirport ??
            (suggested ? { iata: suggested.iata, name: suggested.name } : null),
          airportConfirmed: Boolean(airportConfirmed),
        }}
        onChange={(next) => {
          setValue("shippingMethod", next.method, { shouldValidate: true });
          setValue("shippingAirport", next.airport);
          setValue("shippingAirportConfirmed", next.airportConfirmed);
        }}
      />

      {/* OWN_CARRIER keeps the #235 carrier/trucker capture + the help-me
          concierge escape hatch. DOOR/AIRPORT need no extra fields. */}
      {method === "OWN_CARRIER" ? (
        <div
          data-testid="signup-fulfillment-own-details"
          className="mt-4 border-t border-brand-border pt-4"
        >
          <p className="text-sm font-medium text-zinc-800">
            How should we hand off your boxes in Miami?
          </p>
          <div className="mt-2 grid gap-2">
            {fulfillmentOptions(locale === "es" ? "es" : "en").map((o) => (
              <button
                key={o.value}
                type="button"
                data-testid={`signup-fulfillment-${o.value}`}
                onClick={() =>
                  setValue("fulfillment.choice", o.value, { shouldValidate: true })
                }
                className={`${choice === o.value ? radioCardSelectedClass : radioCardClass} block text-left`}
              >
                <span className="block font-medium">{o.title}</span>
                <span className="mt-0.5 block text-xs text-zinc-500">{o.help}</span>
              </button>
            ))}
          </div>
          {fErr?.choice ? (
            <span className="mt-1 block text-xs text-red-600">
              {fErr.choice.message as string}
            </span>
          ) : null}

          {choice === "trucker" ? (
            <div className="mt-3 space-y-3">
              <label className={labelClass}>
                Trucking company name <span className="text-red-600">*</span>
                <input
                  type="text"
                  data-testid="signup-fulfillment-truckerName"
                  {...register("fulfillment.truckerName")}
                  className={fieldClass}
                />
                {fErr?.truckerName ? (
                  <span className="mt-1 text-xs text-red-600">
                    {fErr.truckerName.message as string}
                  </span>
                ) : null}
              </label>
              <label className={labelClass}>
                Contact phone or email <span className="text-red-600">*</span>
                <input
                  type="text"
                  data-testid="signup-fulfillment-truckerContact"
                  {...register("fulfillment.truckerContact")}
                  className={fieldClass}
                />
                {fErr?.truckerContact ? (
                  <span className="mt-1 text-xs text-red-600">
                    {fErr.truckerContact.message as string}
                  </span>
                ) : null}
              </label>
            </div>
          ) : null}

          {choice === "pickup" ? (
            <p
              data-testid="signup-fulfillment-pickup-hours"
              className="mt-3 text-sm text-zinc-600"
            >
              Pickup hours: <strong>Mon-Sun 7am-2pm EST</strong> at our Doral
              warehouse. Bring photo ID + your order number.
            </p>
          ) : null}

          {/* #434: the "carrier" (own-account) + "help-me" choices were removed
              from the picker - own-account duplicated the priced Door option,
              and clients without a carrier now use Door/Airport. */}
        </div>
      ) : null}
    </div>
  );
}
